import type {
  ICompanyInfo,
  IProduct,
  IProduct2,
  IArticle,
  ITestimonial,
} from "../types/general";
import { addDaysToDate } from "./general";
export const useProductSchema = (product: IProduct2) => {
  //TODO: service levels can be added as variants
  return {
    "@context": "http://schema.org/",
    "@type": "Product",
    name: product?.title,
    image: product?.images?.map((img) => img.url),
    description: product?.description,
    offers: {
      "@type": "Offer",
      price: product?.starting_price?.amount ?? 0,
      priceCurrency: product?.starting_price?.currency.code ?? "-",
      availability: "https://schema.org/InStock",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: product?.rating,
    },
  };
  //   defineLocalBusiness({
  //     type: "EntertainmentBusiness",
  //     name: product?.title,
  //     url: "/",
  //     priceRange: (product?.starting_price?.amount ?? 0) < 100 ? "$" : "$$",
  //     address: {
  //       "@type": "PostalAddress",
  //       addressCountry: product?.points[0]?.city?.country?.name,
  //       addressLocality: product?.points[0]?.city?.name,
  //       streetAddress: product?.points[0]?.address,
  //       priceValidUntil: addDaysToDate(new Date().toDateString(), 10),
  //     },
  //     geo: {
  //       "@type": "GeoCoordinates",
  //       latitude: product?.points[0]?.latitude,
  //       longitude: product?.points[0]?.longitude,
  //     },
  //   }),
  // ]);
};

export const useAboutPageSchema = (info: ICompanyInfo) => {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: info?.organization.name,
    description: info?.short_description,
  };
};

export const useProductListSchema = (
  products: IProduct[],
  name?: string,
  description?: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: name,
    description: description,
    itemListElement: products.map((product) => ({
      url: generateProductUrl(product),
    })),
  };
};

export const useBreadcrumbListSchema = (
  items: { name: string; item: string }[]
) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((breadcrumb, index) => ({
      "@type": "ListItem",
      name: breadcrumb?.name,
      item: breadcrumb?.item,
      position: index + 1,
    })),
  };
};

export const useFAQPageSchema = (
  questions: { title: string; content: string }[]
) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((item) => {
      return {
        "@type": "Question",
        name: item?.title,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.content,
        },
      };
    }),
  };
};

export const useWebPageSchema = (attributes: Record<string, string>) => {
  // useSchemaOrg([
  //   defineWebPage({
  //     name: attributes?.name,
  //     description: attributes?.description,
  //   }),
  // ]);
};

export const useOrganizationSchema = (
  metaTags: any,
  socialMedia: string,
  comments: ITestimonial[]
) => {
  const siteConfig = useSiteConfig();
  const runtimeConfig = useRuntimeConfig();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: metaTags?.title ?? siteConfig.name,
    description: siteConfig.description,
    url: siteConfig.url,
    logo: runtimeConfig.public.logo,
    address: {
      "@type": "PostalAddress",
      streetAddress: siteConfig.company?.address?.address_description,
      addressLocality: siteConfig.company?.address?.city?.name,
      addressCountry: siteConfig.company?.address?.city?.country?.name,
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: siteConfig.company?.telephones?.[0],
      email: siteConfig.company?.email,
    },
    sameAs: socialMedia,
    review: comments?.length
      ? comments.map((comment: ITestimonial) => ({
          "@type": "Review",
          author: comment.name,
          // datePublished: "",
          name: comment.title,
          reviewBody: comment.description,
          reviewRating: {
            "@type": "Rating",
            ratingValue: comment.rate,
          },
        }))
      : [],
  };
};
export const useWebSiteSchema = (metaTags: any) => {
  const siteConfig = useSiteConfig();
  useSeoMeta({
    ogType: "website",
    ogUrl: siteConfig.url,
    ogSiteName: siteConfig.name,
    ogImage: siteConfig.image,
    ogTitle: siteConfig.name,
    ogDescription: metaTags?.description ?? siteConfig.description,
    description: metaTags?.description ?? siteConfig.description,
  });

  // useSchemaOrg([

  //   defineWebSite({
  //     name: siteConfig.name,
  //     url: siteConfig.url,
  //     potentialAction: [
  //       defineSearchAction({
  //         target: "/search?q={search_term_string}",
  //       }),
  //     ],
  //   }),
  // ]);
};

export const useArticleSchema = (article: IArticle): void => {
  // useSchemaOrg([
  //   defineArticle({
  //     "@type": "BlogPosting",
  //     headline: article.SEO.metaTitle,
  //     description: article.SEO.metaDescription,
  //     image: article.imageUrl,
  //     datePublished: article.publishedAt,
  //     dateModified: article.updatedAt,
  //   }),
  // ]);
};

export const usePrivacyPolicySchema = (_: unknown) => {};

export const useTermsAndConditionsSchema = (_: unknown) => {};

const generateProductUrl = (product: IProduct): string => {
  const cities = product?.cities ?? [];
  return `/${replaceSpace(cities[0]?.name ?? null)}/${replaceSpace(product?.slug)}`;
};

const replaceSpace = (item: string | null | undefined) =>
  item?.replace(/\s+/g, "-");

export declare function useSeoSchema(schema: unknown[]): void;
